import { useEffect, useMemo, useState } from 'react';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { fetchUnmappedProductMaps, getUnmappedProductsSubject, UnmappedProduct } from '../../../../../store/ProductMap';
import { DefaultUnmappedProductGridColumnDefs } from '../../ColumnDefs/UnmappedProductGridColumnDefs';
import { UpstackDataGrid } from '../../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getInitialState } from '../../../../Compounds/UpstackDataGrid/helpers';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import {
  getCustomizationSubject,
  getDefaultPreferences,
  GridPreferences
} from '../../../../../store/UserCustomizations';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { NewMapDialog } from '../../../../Compounds/Mapping/NewMapDialog/NewMapDialog';
import { EntityType } from '../../../../../store/Search';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../../../store/GeneralStore';
import { getProductsSubject, Product } from '../../../../../store/Product';

export const UnmappedProductTab = () => {
  const [unmappedProductsLoading, setUnmappedProductsLoading] = useState<boolean>(false);
  const [unmappedProducts, setUnmappedProducts] = useState<PaginatedResponse<UnmappedProduct>>();
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences()?.content['unmappedProductGrid'] as GridPreferences
  );
  const [initialState, setInitialState] = useState<GridInitialState>();
  const [activeProduct, setActiveProduct] = useState<UnmappedProduct | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);

  const openModal = (product: UnmappedProduct) => {
    setActiveProduct(product);
  };

  const columns = DefaultUnmappedProductGridColumnDefs({ openModal });

  const filterKeys = useMemo(
    () => columns.filter((column) => column.filterable).map((column) => column.field),
    [columns]
  );

  const handleFetchProductMaps = async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
    if (localLoading) setUnmappedProductsLoading(true);
    await fetchUnmappedProductMaps(filters, forceRefresh);
    if (localLoading) setUnmappedProductsLoading(false);
  };

  useEffect(() => {
    const unmappedProductsSub = getUnmappedProductsSubject().subscribe((unmappedProducts) =>
      setUnmappedProducts(unmappedProducts)
    );
    const userCustSub = getCustomizationSubject().subscribe((userCust) => {
      if (userCust) {
        setGridSettings(userCust.content.unmappedProductGrid);

        const state = getInitialState(userCust.content.unmappedProductGrid, columns, 'unmappedProductGrid');
        setInitialState(state);

        const savedSort = state.sorting?.sortModel;
        const initialSortModel = savedSort?.length ? savedSort : undefined;
        handleFetchProductMaps(
          {
            orders: initialSortModel
              ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
              : undefined
          },
          true
        );
      }
    });

    const productsSub = getProductsSubject().subscribe((products) => {
      setAllProducts(products);
    });

    return () => {
      if (userCustSub) userCustSub.unsubscribe();
      if (unmappedProductsSub) unmappedProductsSub.unsubscribe();
      if (productsSub) productsSub.unsubscribe();
    };
  }, []);

  return (
    <PageCard
      dataTestId="unmapped-product-management"
      dataCy="unmapped-product-management"
      childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
      title="Unmapped RPM Products (with items in latest run)">
      <>
        {!initialState ||
          (unmappedProductsLoading && (
            <div className="flex h-[45rem] m-auto">
              <ComponentSpinner label="Loading data..." />
            </div>
          ))}
        {initialState && !unmappedProductsLoading && (
          <UpstackDataGrid
            title="Product Map"
            rows={unmappedProducts?.data || []}
            rowCount={unmappedProducts?.totalCount}
            columns={columns}
            advancedFiltering={true}
            apiFilter={true}
            fetchFunction={handleFetchProductMaps}
            filterKeys={filterKeys}
            page="unmappedProductGrid"
            gridSettings={gridSettings}
            showSearch={false}
            initialState={initialState}
            sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
            triggerSave={false}
            rowPrimaryKey="rpmProductId"
          />
        )}
        <NewMapDialog
          open={activeProduct !== null}
          setOpen={(open) => {
            setActiveProduct(open ? activeProduct : null);
            if (!open) {
              handleFetchProductMaps(undefined, true, true);
            }
          }}
          mapType={EntityType.PRODUCT}
          allProducts={allProducts}
          productMaps={[]}
          preFill="RPM"
          productPrefill={{
            rpmSupplierId: activeProduct?.rpmProductSupplierId,
            rpmSupplierName: activeProduct?.rpmProductSupplierName,
            rpmProductId: activeProduct?.rpmProductId,
            rpmProductName: activeProduct?.rpmProductName
          }}
          disabledProductPrefill
        />
      </>
    </PageCard>
  );
};
